import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import intersection from 'lodash/intersection';
import { Alert, Link, Stack, Text } from '@solvhealth/jigsaw-v1';
import Observer from '~/components/SolvPatternLibrary/Profilers/Observer';
import CovidTesting from '../../components/CovidTesting';
import { SELF_PAY_PRICES, shouldShowFeature } from '../../util/customization';
import { LocationContext } from '../../ContextProviders';
import CDPSection from '../CDPSection/index';
import SelfPayAccordion from './SelfPayAccordion';
import { ServicePrices, ServicePrice } from './util/Service';
import { ServiceBookText, ServiceDescription, ServiceName } from './util/Reasons';
import { useServicePrices } from '~/hooks/redux/location';
import { analyticsTrackEvent } from '~/core/analytics';
import { CDP_LEARN_MORE_SELF_PAY_CLICK, CDP_SCROLL_TO_CLEAR_PRICE } from '~/core/analytics/events';
import { StyledSubtext } from '~/components/ClinicDetailPageFlexible/components/CovidTesting/CSSstyles';
import {
  TEXT_FOR_LEGAL_DESC_CPP,
  TEXT_FOR_LEGAL_DESC_NON_CPP,
  TEXT_FOR_SUPERSCRIPT_1,
  TEXT_FOR_SUPERSCRIPT_2,
} from '~/components/ClinicDetailPageFlexible/components/CovidTesting/util';
import { openExternalUrl } from '~/core/util/url';
import { CLEAR_PRICE_PATH } from '~/constants';
import { BASE_URL } from '../../../../config';
import CDPOtherServices from '~/components/ClinicDetailPageFlexible/components/CDPOtherServices';

const Root = styled.div.withConfig({ componentId: 'sc-1pip5vd-0' })`
  -webkit-font-smoothing: antialiased;
  width: 100%;
`;

const covidServices = [
  ServiceName.covid_19_antibody_test,
  ServiceName.covid_19_antigen_test,
  ServiceName.covid_19_pcr_test,
  ServiceName.rapid_covid_19_antigen_test,
  ServiceName.rapid_covid_19_pcr_test,
];

const AlertWrap = styled.div.withConfig({ componentId: 'sc-1pip5vd-1' })`
  width: calc(100% - 32px);
`;

const handleLearnMoreClick = () => {
  analyticsTrackEvent(CDP_LEARN_MORE_SELF_PAY_CLICK);
  openExternalUrl(`${BASE_URL}${CLEAR_PRICE_PATH}`, '_blank');
};

const CppIcon = styled.img.withConfig({ componentId: 'sc-1pip5vd-2' })`
  margin-right: 4px;
  width: 12px;
`;

const handleBecomeAClearPricePartner = () => {
  openExternalUrl(`https://explore.solvhealth.com/clear-price-provider`, '_blank');
};

const LocationCPP = ({
  isClearPricePartner,
  isSolvPartner,
  locationName,
  hasKnownPrices,
}: {
  isClearPricePartner: boolean;
  isSolvPartner: boolean;
  locationName: string;
  hasKnownPrices: boolean;
}) => {
  let clearPricePartnerPrompt = null;
  if (isClearPricePartner) {
    clearPricePartnerPrompt = (
      <Stack alignItems="baseline" row>
        <CppIcon src="/images/icons/clear_price/clear_price_partner.svg" />
        <Text fontSize="xs">
          {locationName && <>{locationName} is a </>}Solv ClearPrice Partner
        </Text>
      </Stack>
    );
  } else if (!isSolvPartner) {
    clearPricePartnerPrompt = (
      <Stack alignItems="baseline" row>
        <Text fontSize="xs">Is this your clinic?</Text>
        <Link
          onClick={() => handleBecomeAClearPricePartner()}
          sx={{ fontSize: 'xs', marginLeft: '4px' }}
        >
          Become a Solv ClearPrice Partner
        </Link>
      </Stack>
    );
  }

  const legalDescription = hasKnownPrices ? (
    <StyledSubtext>
      {isClearPricePartner ? TEXT_FOR_LEGAL_DESC_CPP : TEXT_FOR_LEGAL_DESC_NON_CPP}
    </StyledSubtext>
  ) : null;

  return (
    <>
      {clearPricePartnerPrompt}
      {legalDescription}
    </>
  );
};

const commonServicesKeys: (keyof typeof ServiceName)[] = [
  'base_office_visit',
  'x-ray_extremity',
  'x-ray_chest',
  'flu_shot',
  'camp_physical',
  'school_and_sports_physicals',
  'sports_physicals',
  'laceration_repair',
  'ten_panel_drug_test',
  'five_panel_drug_test',
  'covid_pill',
  'hepatitis_test',
  'monoclonal_antibody_therapy',
  'botox',
  'dermaplaning',
  'dermal_filler',
  'microdermabrasion',
  'mole_removal',
  'wart_removal',
];

type ServiceRichDetails = ServicePrice & {
  id: keyof typeof ServiceName;
  hasKnownPrices?: boolean;
  bookText: string;
  description?: string;
};

const getCommonServicesData = (servicePrices: ServicePrices) => {
  const data: ServiceRichDetails[] = [];
  commonServicesKeys.forEach((key) => {
    if (servicePrices[key]) {
      data.push({
        id: key,
        ...servicePrices[key],
        ...ServiceDescription[key],
        ...ServiceBookText[key],
      });
    }
  });
  return data;
};

const MemoizedSelfPay = React.memo(
  ({
    className,
    servicePrices,
    telemedServicePrices,
    isTelemed = false,
    isClearPricePartner,
    isSolvPartner,
    locationName,
    observer,
  }: {
    className?: string;
    enableCollapsing?: boolean;
    defaultExpanded?: boolean;
    servicePrices: ServicePrices;
    telemedServicePrices: ServicePrices;
    isTelemed?: boolean;
    isClearPricePartner: boolean;
    isSolvPartner: boolean;
    locationName: string;
    observer: boolean;
  }) => {
    const commonServicesRichDataList = getCommonServicesData(servicePrices);

    const { hasKnownPrices } = servicePrices;

    const hasSelfPayCovidServices =
      intersection(Object.keys(servicePrices), covidServices).length > 0;

    const handleScrollToCommonServices = () => {
      analyticsTrackEvent(CDP_SCROLL_TO_CLEAR_PRICE);
    };

    if (commonServicesRichDataList.length > 0 || hasSelfPayCovidServices) {
      const baseOfficeVisit = commonServicesRichDataList.find(
        (service) => service.id === ServiceName.base_office_visit
      );

      const telemedOfficeVisit = telemedServicePrices.base_office_visit
        ? {
            ...telemedServicePrices.base_office_visit,
            description:
              'Connect with a provider from the comfort of your home. Best for the treatment of conditions including cough, chest congestion, skin infections, sore throat, and fever. Many prescription refills are also available.',
            bookText: 'Book a video visit',
          }
        : undefined;

      const commonServices = (
        <CDPSection heading="Common services" isSubsection>
          <Root className={className}>
            <Stack space={3}>
              {isTelemed ? (
                <>
                  {baseOfficeVisit && <SelfPayAccordion {...baseOfficeVisit} name="Video visit" />}
                </>
              ) : (
                <>
                  {baseOfficeVisit && <SelfPayAccordion {...baseOfficeVisit} name="Office visit" />}
                  {telemedOfficeVisit && (
                    <SelfPayAccordion
                      {...telemedOfficeVisit}
                      name="Video visit"
                      useAssociatedTelemed
                    />
                  )}
                </>
              )}

              {commonServicesRichDataList.map((service) => {
                if (service.id === ServiceName.base_office_visit) {
                  return null;
                }
                return (
                  <SelfPayAccordion
                    {...service}
                    baseOfficeVisit={baseOfficeVisit}
                    key={service.id}
                  />
                );
              })}

              <CovidTesting />
            </Stack>
          </Root>
        </CDPSection>
      );

      return (
        <>
          {observer ? (
            <Observer handleSawComponent={handleScrollToCommonServices} threshold={0.25}>
              {commonServices}
            </Observer>
          ) : (
            commonServices
          )}
          <CDPOtherServices />
          {hasKnownPrices && (
            <AlertWrap>
              <Alert
                actions={[
                  <Alert.Action key="learn-more" onClick={handleLearnMoreClick}>
                    Learn more
                  </Alert.Action>,
                ]}
                icon={
                  <img src="/images/icons/clear_price/clear_price_magnifier.svg" width="40px" />
                }
                show
                tone="success"
                variant="inline"
              >
                <Text>
                  Solv is gathering self-pay prices for common services through Solv’s{' '}
                  <Text as="span" fontWeight="bold">
                    ClearPrice
                  </Text>{' '}
                  initiative.
                </Text>
              </Alert>
            </AlertWrap>
          )}
          <Stack>
            <LocationCPP
              hasKnownPrices={!!hasKnownPrices}
              isClearPricePartner={isClearPricePartner}
              isSolvPartner={isSolvPartner}
              locationName={locationName}
            />
            <StyledSubtext>
              <sup>1</sup> {TEXT_FOR_SUPERSCRIPT_1}
            </StyledSubtext>
            <StyledSubtext>
              <sup>2</sup> {TEXT_FOR_SUPERSCRIPT_2}
            </StyledSubtext>
          </Stack>
        </>
      );
    }
    return null;
  }
);

// @ts-expect-error ts-migrate(2339) FIXME: Property 'propTypes' does not exist on type 'Named... Remove this comment to see the full error message
MemoizedSelfPay.propTypes = {
  className: PropTypes.string,
  locationServicePrices: PropTypes.array,
  associatedTelemedLocationServicePrices: PropTypes.array,
  isTelemed: PropTypes.bool,
};

type Props = {
  className?: string;
  observer?: boolean;
};

export const SelfPay = ({ className, observer = true }: Props) => {
  const { location, associatedTelemedLocation } = useContext(LocationContext);

  const allV1ServicePrices = useServicePrices(location, true);
  const telemedServicePrices = useServicePrices(associatedTelemedLocation!);

  if (!shouldShowFeature(SELF_PAY_PRICES, location) || !Object.keys(allV1ServicePrices).length) {
    return null;
  }

  const isTelemed = location?.isTelemed ?? false;

  return (
    <MemoizedSelfPay
      className={className}
      isClearPricePartner={!!location.isClearPricePartner}
      isSolvPartner={!!location.isSolvPartner}
      isTelemed={isTelemed}
      locationName={location.displayNamePrimary}
      observer={observer}
      servicePrices={allV1ServicePrices}
      telemedServicePrices={telemedServicePrices}
    />
  );
};

export default SelfPay;
